import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '@/components/icons';
import { ErrorBoundary } from '@/components/error';
import { ImpersonationBanner } from './ImpersonationBanner';
import { GroupScopeSwitcher } from './GroupScopeSwitcher';
import { Modal } from '@/components/ui/Modal';
import { AssistFab } from '@/components/shared/AssistFab';
import { useSession } from '@/hooks';
import { isPlatformAdmin } from '@/auth/session';
import { GroupScopeProvider, useGroupScope } from '@/context/GroupScopeContext';
import styles from './PlatformLayout.module.css';

/**
 * The console CSKY staff operate the platform from.
 *
 * Deliberately not the institution shell with extra menu items. A platform
 * administrator is not a privileged school user: their actions land on every
 * tenant at once, and the single most expensive mistake available here is
 * believing you are inside one institution when you are not. So the chrome is
 * dark where the institution app is light, the accent is amber where the
 * institution app is blue, and a scope bar that cannot be dismissed states which
 * mode you are in. If someone glances at a screenshot they should know instantly
 * which of the two apps it came from.
 *
 * This component is also the route guard. Hiding the navigation is presentation
 * only — every endpoint behind it re-checks the role against a signed token, so
 * a user who edits their session in the console gets a 403 rather than data.
 */

interface PlatformNavItem {
  to: string;
  icon: IconName;
  labelKey: string;
  /** Matched exactly; otherwise a child path also lights the parent up. */
  end?: boolean;
}

interface PlatformNavGroup {
  labelKey: string;
  items: readonly PlatformNavItem[];
}

/**
 * Grouped, because eighteen flat links is a list nobody reads.
 *
 * The grouping is by what the operator is doing, not by which service owns the
 * endpoint: audit, delegations and support access sit together because they are
 * all answers to "who may do what and who did what", even though two of them are
 * identity's and one spans both services. An operator should never need to know
 * which backend implements a screen in order to find it.
 */
const NAV_GROUPS: readonly PlatformNavGroup[] = [
  {
    labelKey: 'platform.nav.group.work',
    items: [
      { to: '/admin', icon: 'pulse', labelKey: 'platform.nav.overview', end: true },
      { to: '/admin/onboarding', icon: 'checklist', labelKey: 'platform.nav.onboarding' },
      { to: '/admin/institutions', icon: 'institution', labelKey: 'platform.nav.institutions' },
      { to: '/admin/groups', icon: 'building', labelKey: 'platform.nav.groups' },
    ],
  },
  {
    labelKey: 'platform.nav.group.governance',
    items: [
      { to: '/admin/audit', icon: 'history', labelKey: 'platform.nav.audit' },
      { to: '/admin/delegations', icon: 'users', labelKey: 'platform.nav.delegations' },
      { to: '/admin/support-access', icon: 'noAccess', labelKey: 'platform.nav.supportAccess' },
    ],
  },
  {
    labelKey: 'platform.nav.group.configuration',
    items: [
      { to: '/admin/configuration', icon: 'settings', labelKey: 'studio.navLabel' },
      { to: '/admin/institution-templates', icon: 'building', labelKey: 'templates.navLabel' },
      { to: '/admin/grading-strategies', icon: 'academic', labelKey: 'grading.navLabel' },
      { to: '/admin/academic-policies', icon: 'tasks', labelKey: 'acadPolicies.navLabel' },
      { to: '/admin/form-schemas', icon: 'report', labelKey: 'formSchemas.navLabel' },
      { to: '/admin/feature-flags', icon: 'star', labelKey: 'platform.nav.featureFlags' },
      { to: '/admin/integrations', icon: 'integration', labelKey: 'platform.nav.integrations' },
      { to: '/admin/policies', icon: 'admin', labelKey: 'platform.nav.policies' },
      { to: '/admin/master-data', icon: 'products', labelKey: 'platform.nav.masterData' },
    ],
  },
  {
    labelKey: 'platform.nav.group.operations',
    items: [
      { to: '/admin/operations', icon: 'clock', labelKey: 'platform.nav.operations' },
      { to: '/admin/imports', icon: 'external', labelKey: 'platform.nav.imports' },
      { to: '/admin/reports', icon: 'filter', labelKey: 'platform.nav.reports' },
    ],
  },
];

export function PlatformLayout() {
  return (
    <GroupScopeProvider>
      <PlatformConsole />
    </GroupScopeProvider>
  );
}

/**
 * The console itself, inside the group-scope provider.
 *
 * Split out so the F3 scope control (top bar) and the group routes (Outlet)
 * share one "which group am I working on" state without the provider having to
 * be in main.tsx — the institution world has no notion of a group scope, and
 * mounting it there would only ever have offered it to pages that cannot use it.
 */
function PlatformConsole() {
  const { t, i18n } = useTranslation();
  const { status, session, login, logout } = useSession();
  const [railOpen, setRailOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const location = useLocation();
  const { activeGroup } = useGroupScope();

  // A left-over open rail after navigating on a phone hides the page the user
  // just asked for.
  useEffect(() => setRailOpen(false), [location.pathname]);

  if (status === 'loading') {
    return (
      <div className={styles.gate} role="status" aria-live="polite" aria-label={t('common.loading')}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <div className={styles.gate}>
        <div className={styles.gateCard}>
          <p className={styles.gateEyebrow}>{t('platform.shell.eyebrow')}</p>
          <h1 className={styles.gateTitle}>{t('platform.gate.signInTitle')}</h1>
          <p className={styles.gateBody}>{t('platform.gate.signInBody')}</p>
          <button type="button" className={styles.gateCta} onClick={login}>
            {t('auth.signIn')}
          </button>
        </div>
      </div>
    );
  }

  if (!isPlatformAdmin(session?.realmRoles)) {
    return (
      <div className={styles.gate}>
        <div className={styles.gateCard} role="status">
          <p className={styles.gateEyebrow}>{t('platform.shell.eyebrow')}</p>
          <h1 className={styles.gateTitle}>{t('platform.gate.deniedTitle')}</h1>
          <p className={styles.gateBody}>{t('platform.gate.deniedBody')}</p>
          <Link className={styles.gateCta} to="/">
            {t('platform.gate.backToWorkspace')}
          </Link>
        </div>
      </div>
    );
  }

  const toggleLanguage = () => i18n.changeLanguage(i18n.language === 'en' ? 'fr' : 'en');

  return (
    <div className={styles.console}>
      <a className={styles.skipLink} href="#platform-content">
        {t('platform.shell.skipToContent')}
      </a>

      {railOpen && <div className={styles.railOverlay} role="presentation" onClick={() => setRailOpen(false)} />}

      <aside
        className={`${styles.rail} ${railOpen ? styles.railOpen : ''}`}
        aria-label={t('platform.shell.navLabel')}
      >
        <div className={styles.railHeader}>
          <span className={styles.railMark} aria-hidden="true">
            <Icon name="platform" size={20} />
          </span>
          <span className={styles.railWordmark}>
            <strong>{t('platform.shell.wordmark')}</strong>
            <span>{t('platform.shell.wordmarkSub')}</span>
          </span>
          <button
            type="button"
            className={styles.railClose}
            onClick={() => setRailOpen(false)}
            aria-label={t('common.close')}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <nav className={styles.railNav} aria-label={t('platform.shell.navLabel')}>
          {NAV_GROUPS.map((group) => (
            <div key={group.labelKey} className={styles.railGroup}>
              <h2 className={styles.railGroupLabel}>{t(group.labelKey)}</h2>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `${styles.railLink} ${isActive ? styles.railLinkActive : ''}`}
                >
                  <Icon name={item.icon} size={18} />
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className={styles.railFooter}>
          {/* Platform staff also hold ordinary institution roles. Without a way
              back they have to guess a URL to leave the console. */}
          <Link className={styles.railFooterBtn} to="/">
            <Icon name="switch" size={16} />
            {t('platform.shell.exitToWorkspace')}
          </Link>
          <button type="button" className={styles.railFooterBtn} onClick={toggleLanguage}>
            <Icon name="globe" size={16} />
            {i18n.language === 'en' ? 'Français' : 'English'}
          </button>
          <button
            type="button"
            className={`${styles.railFooterBtn} ${styles.railFooterBtnDanger}`}
            onClick={() => setSignOutOpen(true)}
          >
            <Icon name="logout" size={16} />
            {t('auth.signOut')}
          </button>
        </div>
      </aside>

      <div className={styles.body}>
        <header className={styles.topbar}>
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setRailOpen(true)}
            aria-label={t('common.menu')}
          >
            <Icon name="menu" size={22} />
          </button>
          <div className={styles.topbarIdentity}>
            <span className={styles.topbarEyebrow}>{t('platform.shell.eyebrow')}</span>
            <span className={styles.topbarOperator}>{session?.name || session?.email || ''}</span>
          </div>
          <GroupScopeSwitcher />
          <span className={styles.operatorBadge}>{t('platform.shell.roleBadge')}</span>
        </header>

        {/* Above the router, so no route can unmount it and no navigation can
            lose it. Renders nothing unless a support session is open. */}
        <ImpersonationBanner />

        {/* Not dismissible. The whole risk of this console is forgetting that
            what you are looking at is every institution, not one. A group scope
            narrows that to one group's institutions — and says so, in the same
            colour, for the same reason. */}
        <div className={styles.scopeBar} role="note">
          <Icon name="warning" size={16} />
          <span className={styles.scopeText}>
            {activeGroup ? (
              <>
                <strong>{t('platform.groups.scope.scopeBarTitle')}</strong>{' '}
                {t('platform.groups.scope.scopeBarBody', { groupName: activeGroup.groupName ?? '' })}
              </>
            ) : (
              <>
                <strong>{t('platform.shell.scopeTitle')}</strong> {t('platform.shell.scopeBody')}
              </>
            )}
          </span>
        </div>

        <main className={styles.main} id="platform-content" tabIndex={-1}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      <Modal
        open={signOutOpen}
        onClose={() => setSignOutOpen(false)}
        title={t('auth.signOutTitle')}
        description={t('auth.signOutDescription')}
      >
        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={() => setSignOutOpen(false)}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="modal-button modal-button--danger"
            onClick={() => {
              setSignOutOpen(false);
              logout();
            }}
          >
            {t('auth.signOut')}
          </button>
        </div>
      </Modal>
      <AssistFab />
    </div>
  );
}
